import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { decrypt, encrypt } from 'utils/Crypto';
import { AccountStatus, AdminRole, ProcessStatus } from 'interfaces';
import { immer } from 'zustand/middleware/immer';

type Admin = {
  admin_id: number;
  shop_information_id: number;
  admin_position: string;
  admin_level: number;
  admin_privilege: number;
  type: (typeof AdminRole)[keyof typeof AdminRole];
  account_status: (typeof AccountStatus)[keyof typeof AccountStatus];
  pw_change_time: number | null;
};

type User = {
  user_id: number;
  grade_id: number | null;
  user_account: string;
  user_name: string;
  user_phone: string;
  user_email: string;
  user_gender: string | null;
  user_address: string;
  user_address_detail: string | null;
  user_setting: Record<string, any>;
  access_time: number | null;
  withdrawal_time: number | null;
  account_status: (typeof AccountStatus)[keyof typeof AccountStatus];
  pw_change_time: number | null;
  user_birthday: number | null;
  user_nickname: string | null;
  postcode: string | null;
  kakao_id: string | null;
  google_id: string | null;
  naver_id: string | null;
  facebook_id: string | null;
  apple_id: string | null;
  created_at: number;
  updated_at: number;
  type: string;
};

type Shop = Admin & {
  mall_information: Record<string, any> | null;
  store_information: Record<string, any> | null;
  shop_information_name: string;
  shop_information_content: string;
  shop_information_address: string;
  shop_information_address_detail: string;
  application_status: (typeof ProcessStatus)[keyof typeof ProcessStatus];
  correction_status: (typeof ProcessStatus)[keyof typeof ProcessStatus];
  edit_information: Record<string, any>;
  commission_id: number | null;
  latitude: number | null;
  longitude: number | null;
};

type UserState = {
  user: Admin | User | Shop | null;

  actions: {
    setUser: (data: Admin | User | Shop | null) => void;
    getUser: () => void;
    resetUser: () => void;
    fetchUser: (key: string, data: Object[] | null) => void;
  };
};

type AccountState = {
  account: string | null;
  actions: {
    setAccount: (id: string) => void;
    removeAccount: () => void;
  };
};

const useUserStore = create<UserState>()(
  persist(
    immer((set, get) => ({
      user: null,
      account: null,
      actions: {
        setUser: (userInfo) => {
          const data = JSON.stringify(userInfo);
          const encodeData = encrypt(data);
          set({ user: encodeData });
        },
        getUser: () => {
          const userInfo = get().user;
          const decodeData = userInfo ? JSON.parse(decrypt(userInfo)) : null;
          return decodeData;
        },
        resetUser: () => set({ user: null }),
        fetchUser: (key, data) => {
          set((state) => {
            const userData = state.user ?? {};

            return {
              ...state,
              user: {
                ...userData,
                [key]: data,
              },
            };
          });
        },
      },
    })),
    {
      name: 'jm-auth-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ user: state.user }),
    }
  )
);

const useAccountStore = create<AccountState>()(
  persist(
    immer((set, get) => ({
      account: null,
      actions: {
        setAccount: (id) => {
          const encodeData = encrypt(id);
          set({ account: encodeData });
        },
        removeAccount: () => set({ account: null }),
      },
    })),
    {
      name: 'jm-account-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ account: state.account }),
    }
  )
);

export const useUserAction = () => useUserStore((store) => store.actions);
export const useUserStack = () =>
  useUserStore((store) =>
    store.user ? JSON.parse(decrypt(store.user)) : null
  );
export const useUserState = () => useUserStore.getState();

export const useAccountAction = () => useAccountStore((store) => store.actions);
export const useAccountStack = () =>
  useAccountStore((store) => (store.account ? decrypt(store.account) : null));
export const useAccountState = () => useAccountStore.getState();
